import { selectedLocale, t } from '#lib/stores/i18n.js';
import {
	BookingStatus,
	type CustomFieldWidget,
	type CustomerPackageUsage,
	type GetAvailabilityRequest,
	type WidgetActivity,
	type WidgetAvailability,
	type WidgetBookingSummary,
	type WidgetBookRequest,
	type WidgetOffer,
	type WidgetPriceList,
	type WidgetProposal
} from '#lib/types/generated/index.js';
import {
	apiAppWidgetAvailabilityGetavailability,
	apiWidgetBook,
	apiWidgetCancel,
	apiWidgetGetBooking,
	apiWidgetPrice
} from '#lib/types/generated/api.gen.js';
import { widgetConfig, widgetContent } from '#lib/widget/core.js';
import { widgetAuthenticatedMember } from '#lib/widget/member.js';
import { computeWidgetBookingPackageUsages } from '#lib/widget/packages/packages.js';
import { DateTime } from 'luxon';
import { derived, get, writable } from 'svelte/store';

export type WidgetPrice = WidgetPriceList['prices'][string];

export type BookingSession = NonNullable<WidgetBookRequest['sessions']>[number] & {
	activityId: string;
};

export type WidgetBookingCustomer = {
	id?: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	phoneOptIn: boolean;
	emailOptIn: boolean;
	preferredLocale: string;
	customFields: Record<string, string>;
};

export type WidgetBooking = {
	id: string;
	activity?: WidgetActivity;
	pax?: number;
	date?: DateTime;
	offer?: WidgetOffer;
	collectionId?: string;
	customer: WidgetBookingCustomer;
	note?: string;
	prices: Record<string, number>;
	status: BookingStatus;
	paymentType?: 'full' | 'deposit' | 'guarantee' | 'damage' | 'split';
	sessions: BookingSession[];
	customFields: Record<CustomFieldWidget['id'], string>;
	products: Record<string, number>;
	packageUsages: CustomerPackageUsage[];
	hasAcceptedTerms: boolean;
};

export type WidgetFilter = {
	minTime: number | undefined;
	maxTime: number | undefined;
};

export function newWidgetBooking(): WidgetBooking {
	return {
		id: '',
		customer: {
			id: '',
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
			phoneOptIn: false,
			emailOptIn: false,
			customFields: {},
			preferredLocale: get(selectedLocale)
		},
		prices: {},
		status: BookingStatus.Intent,
		sessions: [],
		customFields: {},
		products: {},
		packageUsages: [],
		hasAcceptedTerms: false
	};
}

export const widgetPrices = writable<WidgetPrice[] | undefined>(undefined);
export const widgetBookingExpiresAt = writable<DateTime | undefined>(undefined);
export const widgetBookingSaved = writable<boolean>(false);
export const widgetCustomerSaved = writable<boolean>(false);
export const widgetBookingError = writable<string | undefined>(undefined);
export const widgetPayment = writable<WidgetBookingSummary | undefined>(undefined);

export function setWidgetBookingError(errorMessage: string | undefined): void {
	if (!errorMessage) {
		widgetBookingError.set(undefined);
		return;
	}

	if (errorMessage === 'Session not available') {
		widgetBookingError.set(get(t)('widget-external.error.session_not_available'));
		return;
	}

	widgetBookingError.set(errorMessage);
}

export function resetWidgetPayment(): void {
	widgetPayment.set(undefined);
}

export const widgetBooking = writable<WidgetBooking>(newWidgetBooking());
export const widgetCustomerHasAcceptedOffer = writable<WidgetOffer['id'] | undefined>();

export const widgetBookingCancellationDate = derived(
	[widgetPayment, widgetBooking],
	([$widgetPayment, $widgetBooking]) => {
		if (!$widgetPayment || !$widgetBooking.date) return undefined;

		if ($widgetPayment.cancellationTimeBefore === null) {
			return null;
		}

		if ($widgetPayment.cancellationTimeBefore === 0) {
			return undefined;
		}

		const firstSession = $widgetBooking.sessions
			.slice()
			.sort((a, b) => a.startAt - b.startAt)
			.at(0);
		const minutes = firstSession ? firstSession.startAt : 0;

		return $widgetBooking.date
			.plus({ minutes })
			.minus({ minutes: $widgetPayment.cancellationTimeBefore });
	}
);

export const widgetAvails = writable<WidgetAvailability>({ offers: {}, memory: null });

export function retrieveAvailForDate(date: DateTime, currentBooking: WidgetBooking): Promise<void> {
	const out: WidgetAvailability = { offers: {}, memory: null };

	return Promise.all([
		requestAvails(
			date.startOf('month'),
			date.startOf('month').plus({ days: 10 }),
			currentBooking
		).then((result) => {
			out.offers = {
				...out.offers,
				...result.offers
			};
		}),
		requestAvails(
			date.startOf('month').plus({ days: 11 }),
			date.startOf('month').plus({ days: 20 }),
			currentBooking
		).then((result) => {
			out.offers = {
				...out.offers,
				...result.offers
			};
		}),
		requestAvails(
			date.startOf('month').plus({ days: 21 }),
			date.endOf('month'),
			currentBooking
		).then((result) => {
			out.offers = {
				...out.offers,
				...result.offers
			};
		})
	]).then(() => {
		const monthPrefix = date.toFormat('yyyy-MM-');
		const preservedOffers = Object.fromEntries(
			Object.entries(get(widgetAvails).offers).filter(
				([dateKey]) => !dateKey.startsWith(monthPrefix)
			)
		);
		widgetAvails.set({
			offers: {
				...preservedOffers,
				...out.offers
			},
			memory: out.memory ?? get(widgetAvails).memory ?? null
		});
	});
}

let cacheAvails: Record<string, WidgetAvailability | undefined> = {};
// Barrier promise that resolves once any in-flight DELETE /booking/{id} (cancelBookingIntent) completes.
// When navigating back from /book/price to /book/hub, freeBooking() fires cancelBookingIntent() without
// awaiting and immediately clears $widgetBooking.id. Waiting on this barrier before fetching availability
// or creating a new booking intent ensures the backend has committed the deletion so the user's previous
// intent does not block their own slot.
let inFlightCancelPromise: Promise<void> = Promise.resolve();

export function clearCacheAvails(): void {
	cacheAvails = {};
	widgetAvails.set({ offers: {}, memory: null });
}

function requestAvails(
	from: DateTime,
	to: DateTime,
	currentBooking: WidgetBooking
): Promise<WidgetAvailability> {
	const params: GetAvailabilityRequest = {
		startDate: from.toISODate(),
		endDate: to.toISODate(),
		pax: currentBooking.pax,
		activityId: currentBooking.activity?.id,
		bookingId: currentBooking.id
	};
	const key = JSON.stringify({ ...params, bookingId: undefined });
	if (cacheAvails[key]) {
		return Promise.resolve(cacheAvails[key]);
	}

	return inFlightCancelPromise.then(() =>
		apiAppWidgetAvailabilityGetavailability(get(widgetConfig).hash, params).then((res) => {
			cacheAvails[key] = res;
			return res;
		})
	);
}

export function retrieveAvailForDates(
	dates: string[],
	currentBooking: WidgetBooking,
	paxOverride?: number
): Promise<void> {
	if (dates.length === 0) return Promise.resolve();

	const effectivePax = paxOverride ?? currentBooking.pax;
	const params: GetAvailabilityRequest = {
		dates,
		pax: effectivePax,
		activityId: currentBooking.activity?.id,
		bookingId: currentBooking.id
	};

	const key = JSON.stringify({ ...params, bookingId: undefined });
	const req = cacheAvails[key]
		? Promise.resolve(cacheAvails[key]!)
		: inFlightCancelPromise.then(() =>
				apiAppWidgetAvailabilityGetavailability(get(widgetConfig).hash, params).then(
					(res) => {
						cacheAvails[key] = res;
						return res;
					}
				)
			);

	return req.then((result) => {
		const requestedDates = new Set(dates);
		const preservedOffers = Object.fromEntries(
			Object.entries(get(widgetAvails).offers).filter(
				([dateKey]) => !requestedDates.has(dateKey)
			)
		);
		widgetAvails.set({
			offers: {
				...preservedOffers,
				...result.offers
			},
			memory: result.memory ?? get(widgetAvails).memory ?? null
		});
	});
}

export const widgetFilters = writable<WidgetFilter>({
	minTime: undefined,
	maxTime: undefined
});

function hasOffer(
	date: DateTime,
	offerId: string | undefined,
	offers: WidgetAvailability['offers']
): boolean {
	const d = date.toISODate();
	if (!d) return true;
	if (!(d in offers)) return false;
	return offerId ? !!offers[d][offerId] : Object.keys(offers[d]).length > 0;
}

export const hasTimeslot = derived(
	[widgetAvails, widgetFilters, widgetBooking],
	([$widgetAvails, $widgetFilters, _$widgetBooking]) =>
		(date: DateTime, offerId: WidgetOffer['id'] | undefined): boolean => {
			const offers = $widgetAvails.offers;
			const minTime = $widgetFilters.minTime;
			const maxTime = $widgetFilters.maxTime;

			if (!minTime && !maxTime) return hasOffer(date, offerId, offers);
			const d = date.toISODate();
			if (!d || !(d in offers)) return false;
			return Object.keys(offers[d])
				.filter((oId) => !offerId || oId === offerId)
				.some((oId) =>
					offers[d][oId].some(
						(proposal) =>
							(!$widgetFilters.minTime || proposal.start >= $widgetFilters.minTime) &&
							(!$widgetFilters.maxTime || proposal.end <= $widgetFilters.maxTime)
					)
				);
		}
);

let cacheKeyPrices: string = '';
let inFlightPricesKey: string = '';
let inFlightPricesPromise: Promise<void> | undefined = undefined;

export function getPricesFromApi(
	offerId: WidgetOffer['id'] | undefined = undefined
): Promise<void> {
	const booking = get(widgetBooking);
	const targetOfferId = offerId ?? booking.offer?.id;
	const targetDate = booking.date?.toISODate();

	if (!targetOfferId || !targetDate) {
		console.error(
			`[getPricesFromApi] Missing required parameter: offerId=${targetOfferId ?? 'undefined'}, date=${targetDate ?? 'undefined'}`,
			{ offerId, booking }
		);
		return Promise.resolve();
	}

	const key = `${targetOfferId}.${targetDate}`;
	if (cacheKeyPrices === key) {
		return Promise.resolve();
	}

	if (inFlightPricesKey === key && inFlightPricesPromise) {
		return inFlightPricesPromise;
	}

	inFlightPricesKey = key;
	widgetPrices.set(undefined);
	const promise = apiWidgetPrice(get(widgetConfig).hash, {
		offerId: targetOfferId,
		date: targetDate
	})
		.then((res) => {
			if (inFlightPricesKey !== key) return;
			cacheKeyPrices = key;
			const availablePrices: WidgetPrice[] = Object.values(res.prices);
			widgetPrices.set(availablePrices);
		})
		.catch((err) => {
			console.error('[getPricesFromApi] Failed to fetch prices', err);
		})
		.finally(() => {
			if (inFlightPricesKey === key) {
				inFlightPricesKey = '';
				inFlightPricesPromise = undefined;
			}
		});

	inFlightPricesPromise = promise;
	return promise;
}

widgetPrices.subscribe((value) => {
	if (value === undefined) {
		cacheKeyPrices = '';
	}
});

export function widgetUpdateContext(previous?: WidgetBooking): Promise<void> {
	const current = get(widgetBooking);

	if (previous) {
		const prevAvailKey = `${previous.activity?.id ?? ''}:${previous.pax ?? ''}`;
		const nextAvailKey = `${current.activity?.id ?? ''}:${current.pax ?? ''}`;
		if (prevAvailKey !== nextAvailKey && current.sessions.length === 0) {
			widgetAvails.set({ offers: {}, memory: null });
		}
	}

	const prevPriceKey =
		previous?.offer?.id && previous.date
			? `${previous.offer.id}.${previous.date.toISODate()}`
			: '';
	const nextPriceKey =
		current.offer?.id && current.date ? `${current.offer.id}.${current.date.toISODate()}` : '';

	if (nextPriceKey === '') {
		if (prevPriceKey !== '' || get(widgetPrices) !== undefined) {
			widgetPrices.set(undefined);
		}
		return Promise.resolve();
	}

	if (nextPriceKey !== prevPriceKey || cacheKeyPrices !== nextPriceKey) {
		return getPricesFromApi(current.offer!.id);
	}

	return Promise.resolve();
}

export function widgetBookingSetActivity(activity: WidgetActivity | undefined): Promise<void> {
	const previous = { ...get(widgetBooking) };
	widgetBooking.update((b) => ({
		...b,
		activity
	}));
	return widgetUpdateContext(previous);
}

export function widgetBookingSetOffer(offer: WidgetOffer | undefined): Promise<void> {
	const previous = { ...get(widgetBooking) };
	widgetBooking.update((b) => ({
		...b,
		offer,
		prices: previous.offer?.id !== offer?.id ? {} : b.prices,
		packageUsages: previous.offer?.id !== offer?.id ? [] : b.packageUsages
	}));
	return widgetUpdateContext(previous);
}

export function widgetBookingSetCollection(collectionId: string | undefined): Promise<void> {
	const previous = { ...get(widgetBooking) };
	widgetBooking.update((b) => ({
		...b,
		collectionId,
		offer: collectionId ? undefined : b.offer
	}));
	return widgetUpdateContext(previous);
}

export function widgetBookingSetPax(pax: number | undefined): Promise<void> {
	const previous = { ...get(widgetBooking) };
	widgetBooking.update((b) => ({
		...b,
		pax
	}));
	return widgetUpdateContext(previous);
}

export function widgetBookingSetDate(date: DateTime | undefined): Promise<void> {
	const previous = { ...get(widgetBooking) };
	widgetBooking.update((b) => ({
		...b,
		date
	}));
	return widgetUpdateContext(previous);
}

export function widgetBookingSetProposal(
	offer: WidgetOffer,
	proposal: WidgetProposal,
	date?: DateTime
): Promise<void> {
	const previous = { ...get(widgetBooking) };
	const content = get(widgetContent);
	widgetBookingSaved.set(true);
	widgetBooking.update((b) => ({
		...b,
		offer,
		date: date ?? b.date,
		prices: previous.offer?.id !== offer.id ? {} : b.prices,
		packageUsages: previous.offer?.id !== offer.id ? [] : b.packageUsages,
		sessions: proposal.sessions.map((slot) => ({
			id: slot.id,
			startAt: slot.start,
			endAt: slot.end,
			activityId:
				slot.id in content.sessions
					? content.sessions[slot.id].activityId
					: (b.activity?.id ?? '')
		}))
	}));
	return widgetUpdateContext(previous);
}

export function widgetBookingReset(): void {
	const previous = { ...get(widgetBooking) };
	widgetBooking.set(newWidgetBooking());
	void widgetUpdateContext(previous);
}

export function widgetBookingSet(booking: WidgetBooking): Promise<void> {
	const previous = { ...get(widgetBooking) };
	widgetBooking.set(booking);
	return widgetUpdateContext(previous);
}

export function widgetBookingSetSessions(sessions: BookingSession[]): void {
	widgetBooking.update((b) => ({
		...b,
		sessions
	}));
}

export function widgetBookingSetPriceQuantity(priceId: string, quantity: number): void {
	widgetBooking.update((b) => ({
		...b,
		prices: {
			...b.prices,
			[priceId]: quantity
		}
	}));
}

export function widgetBookingSetPackageUsages(packageUsages: CustomerPackageUsage[]): void {
	widgetBooking.update((b) => ({
		...b,
		packageUsages
	}));
}

export function widgetBookingSetProducts(products: Record<string, number>): void {
	widgetBooking.update((b) => ({
		...b,
		products
	}));
}

export function widgetBookingSetProductQuantity(productId: string, quantity: number): void {
	widgetBooking.update((b) => {
		if (quantity === 0) {
			const { [productId]: _, ...rest } = b.products;
			return {
				...b,
				products: rest
			};
		}
		return {
			...b,
			products: {
				...b.products,
				[productId]: quantity
			}
		};
	});
}

export function widgetBookingSetCustomer(customer: WidgetBookingCustomer): void {
	widgetBooking.update((b) => ({
		...b,
		customer: { ...customer }
	}));
}

export function widgetBookingSetCustomFields(customFields: Record<string, string>): void {
	widgetBooking.update((b) => ({
		...b,
		customFields: { ...customFields }
	}));
}

export function widgetBookingSetNote(note: string | undefined): void {
	widgetBooking.update((b) => ({
		...b,
		note
	}));
}

export function widgetBookingSetAcceptedTerms(hasAcceptedTerms: boolean): void {
	widgetBooking.update((b) => ({
		...b,
		hasAcceptedTerms
	}));
}

export function widgetBookingSetStatus(status: BookingStatus): void {
	widgetBooking.update((b) => ({
		...b,
		status
	}));
}

export function widgetBookingSetPaymentType(paymentType: WidgetBooking['paymentType']): void {
	widgetBooking.update((b) => ({
		...b,
		paymentType
	}));
}

export function getPricesAvailables(): WidgetPrice[] {
	return get(widgetPrices)!.filter((price) => {
		const booking = get(widgetBooking);

		if (booking.pax && price.minPax > booking.pax) {
			return false;
		}
		if (booking.pax && price.maxPax < booking.pax) {
			return false;
		}

		const time = bookingGetStartTime(booking);
		if (!time) {
			return false;
		}

		return Object.values(price.timePeriods).some(
			(timePeriod) => time >= timePeriod.start && time <= timePeriod.end
		);
	});
}

export const widgetOffers = derived(
	[widgetBooking, widgetContent],
	([$widgetBooking, $widgetContent]): WidgetOffer[] => {
		return Object.values($widgetContent.offers)
			.filter(
				(o) => $widgetBooking.activity && o.activityIds.includes($widgetBooking.activity.id)
			)
			.sort((a, b) => a.sortOrder - b.sortOrder);
	}
);

export function customFieldConcernBooking(
	customField: CustomFieldWidget,
	booking: WidgetBooking
): boolean {
	if (booking.pax !== undefined) {
		if (customField.applyMinPax !== null && booking.pax < customField.applyMinPax) {
			return false;
		}
		if (customField.applyMaxPax !== null && booking.pax > customField.applyMaxPax) {
			return false;
		}
	}

	if (customField.applyOnAllBookings) {
		return true;
	}

	if (
		customField.applyOnOffers.length > 0 &&
		booking.offer &&
		customField.applyOnOffers.includes(booking.offer.id)
	) {
		return true;
	}

	if (
		customField.applyOnSessions.length > 0 &&
		booking.sessions.some((s) => customField.applyOnSessions.includes(s.id))
	) {
		return true;
	}

	if (
		customField.applyOnActivities.length > 0 &&
		booking.sessions.some((s) => customField.applyOnActivities.includes(s.activityId))
	) {
		return true;
	}

	return false;
}

export const hasCustomFields = derived(
	[widgetContent, widgetBooking],
	([$widgetContent, $widgetBooking]) => {
		return (
			$widgetContent.customFields
				.filter((f) => !f.attachToCustomer)
				.filter((cf) => customFieldConcernBooking(cf, $widgetBooking)).length > 0 ||
			$widgetContent.customFields.filter((f) => f.attachToCustomer).length > 0
		);
	}
);

export function bookingGetStartTime(booking: {
	sessions: Array<{ startAt: number }>;
}): number | undefined {
	if (booking.sessions.length === 0) return undefined;
	return Math.min(...booking.sessions.map((session) => session.startAt));
}

export function computeTotal(booking: WidgetBooking): number {
	let total = booking.offer?.basePrice ?? 0;

	const { pricesUsedByPackages } = computeWidgetBookingPackageUsages(
		booking,
		get(widgetAuthenticatedMember)?.customerPackages ?? []
	);

	if (pricesUsedByPackages[''] && booking.offer?.basePrice) {
		total -= booking.offer.basePrice;
	}

	Object.entries(booking.prices).forEach(([priceId, quantity]) => {
		const price = get(widgetPrices)?.find((price) => price.id === priceId);
		if (price) {
			const usedByPackages = pricesUsedByPackages[priceId] ?? 0;
			const remainingToPay = Math.max(0, quantity - usedByPackages);
			total += price.price * remainingToPay;
		}
	});
	Object.entries(booking.products).forEach(([productId, quantity]) => {
		const product = get(widgetContent).products.find((p) => p.id === productId);
		if (product) {
			total += product.price * quantity;
		}
	});
	return total;
}

export function upsertBookingIntent(termsAccepted?: boolean): Promise<void> | undefined {
	const booking: WidgetBooking = get(widgetBooking);
	if (!booking.activity || !booking.pax || !booking.date || !booking.offer) return;

	const formattedDate = booking.date.toISODate();
	const packageUsages: Record<string, number> = {};
	if (Array.isArray(booking.packageUsages)) {
		booking.packageUsages.forEach((u) => {
			if (u.compositionId && u.quantity > 0) {
				const key = String(u.compositionId);
				packageUsages[key] = (packageUsages[key] ?? 0) + u.quantity;
			}
		});
	}

	const hasAcceptedTerms = Boolean(termsAccepted ?? booking.hasAcceptedTerms);

	const payload: WidgetBookRequest = {
		bookingId: booking.id,
		pax: booking.pax,
		date: formattedDate,
		sessions: booking.sessions.map((session) => ({
			id: session.id,
			startAt: session.startAt,
			endAt: session.endAt
		})),
		offerId: booking.offer.id,
		note: booking.note,
		customer: !booking.customer.firstName
			? undefined
			: {
					firstName: booking.customer.firstName,
					lastName: booking.customer.lastName,
					email: booking.customer.email,
					phone: booking.customer.phone,
					phoneOptIn: booking.customer.phoneOptIn,
					emailOptIn: booking.customer.emailOptIn,
					customFields: booking.customer.customFields
				},
		prices: booking.prices,
		customFields: !booking.customer.firstName ? undefined : booking.customFields,
		products: booking.products,
		packageUsages,
		hasAcceptedTerms
	};

	if (booking.hasAcceptedTerms) {
		widgetBooking.update((b) => ({
			...b,
			hasAcceptedTerms: false
		}));
	}

	return inFlightCancelPromise
		.then(() => apiWidgetBook(get(widgetConfig).hash, payload))
		.then((result) => {
			if (!booking.id) {
				widgetBooking.set({
					...booking,
					id: result.bookingId,
					hasAcceptedTerms: false
				});
				widgetBookingExpiresAt.set(DateTime.now().plus({ minutes: 14 }));
			}
		});
}

// Cancels a temporary booking intent on the backend and updates the inFlightCancelPromise barrier
// so subsequent availability or booking requests wait for the deletion to complete.
export function cancelBookingIntent(id: string | undefined): Promise<void> {
	if (!id) return inFlightCancelPromise;
	const cancelRequest = apiWidgetCancel(get(widgetConfig).hash, id)
		.then(() => {})
		.catch((err) => {
			console.error('[cancelBookingIntent] Failed to cancel booking intent', err);
		});
	inFlightCancelPromise = Promise.all([inFlightCancelPromise, cancelRequest]).then(() => {});
	return inFlightCancelPromise;
}

export let bookingIsLoading = false;
export const loadBookingIntent = (id: string): Promise<void> | undefined => {
	if (bookingIsLoading) return;
	bookingIsLoading = true;

	return apiWidgetGetBooking(get(widgetConfig).hash, id)
		.then((result) => {
			const content = get(widgetContent);
			const offer = result.offerId ? content.offers[result.offerId] : undefined;
			const activity = offer
				? content.activities.find((activity) => offer.activityIds.includes(activity.id))
				: undefined;
			const bookingData: WidgetBooking = {
				id: result.id,
				pax: result.pax,
				note: result.note ?? undefined,
				date: result.date,
				status: result.status as BookingStatus,
				customer: {
					id: '',
					...result.customer,
					phoneOptIn: false,
					emailOptIn: false,
					preferredLocale: get(selectedLocale)
				},
				customFields: result.customFields,
				products: {},
				packageUsages: result.packageUsages,
				sessions: result.sessions.map((s) => ({
					id: s.id,
					activityId: '',
					startAt: s.startAt,
					endAt: s.endAt ?? s.startAt
				})),
				prices: result.prices,
				activity,
				offer,
				hasAcceptedTerms: false
			};
			widgetBooking.set(bookingData);
			bookingIsLoading = false;
		})
		.catch((e) => {
			console.error(e);
			throw e;
		});
};
